import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Footer from "../components/Footer";
import "./AdminOrdersPage.css";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

const STATUS_OPTIONS = ["pending", "confirmed", "dispatched", "delivered"];

const STATUS_LABELS = {
  pending: "Pending",
  confirmed: "Confirmed",
  dispatched: "Dispatched",
  delivered: "Delivered",
};

const AdminOrdersPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate("/");
      return;
    }
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchOrders = async () => {
    try {
      const res = await fetch(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch orders");
      const data = await res.json();
      setOrders(data);
    } catch (err) {
      setError("Could not load orders. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    setUpdating(orderId);
    try {
      const res = await fetch(`${API}/admin/orders/${orderId}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error("Update failed");
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o))
      );
    } catch {
      alert("Failed to update status. Please try again.");
    } finally {
      setUpdating(null);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-container">
        <h1 className="admin-title">All Orders</h1>

        {loading && <p className="admin-loading">Loading orders...</p>}
        {error && <p className="admin-error">{error}</p>}

        {!loading && !error && orders.length === 0 && (
          <p className="admin-empty">No orders placed yet.</p>
        )}

        {!loading && orders.map((order) => (
          <div className="admin-order-card" key={order.id}>
            <div className="admin-order-header">
              <div className="admin-order-meta">
                <span className="admin-order-id">Order #{order.id}</span>
                <span className="admin-order-date">
                  {new Date(order.created_at).toLocaleString("en-IN", {
                    day: "numeric", month: "short", year: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  })}
                </span>
              </div>
              <div className="admin-status-control">
                <span className={`admin-status-badge status-${order.status}`}>
                  {STATUS_LABELS[order.status] || order.status}
                </span>
                <select
                  className="admin-status-select"
                  value={order.status}
                  disabled={updating === order.id}
                  onChange={(e) => handleStatusChange(order.id, e.target.value)}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="admin-order-body">
              {/* Customer info */}
              <div className="admin-customer-info">
                <h3 className="admin-section-label">Customer</h3>
                <p className="admin-customer-name">{order.customer_name}</p>
                <p className="admin-customer-detail">{order.customer_phone}</p>
                <p className="admin-customer-detail">{order.customer_email}</p>
              </div>

              {/* Delivery address */}
              <div className="admin-address-info">
                <h3 className="admin-section-label">Delivery Address</h3>
                {order.delivery_address ? (
                  <>
                    <p>{order.delivery_address.street}</p>
                    <p>{order.delivery_address.city}, {order.delivery_address.state} – {order.delivery_address.pin_code}</p>
                  </>
                ) : (
                  <p className="admin-customer-detail">Not provided</p>
                )}
              </div>

              {/* Products */}
              <div className="admin-items-info">
                <h3 className="admin-section-label">Products</h3>
                <table className="admin-items-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Qty</th>
                      <th>Price</th>
                      <th>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(order.items || []).map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.name}</td>
                        <td>{item.quantity}</td>
                        <td>₹{item.price}</td>
                        <td>₹{item.price * item.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="admin-order-total">Total: ₹{order.total_amount}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Footer />
    </div>
  );
};

export default AdminOrdersPage;
